import { useState } from 'react';
import Link from 'next/link';
import ShopifySEO from '../../components/shopify/ShopifySEO';
import { shopifyPages } from '../../content/shopify';
import {
  getGuideUi,
  guideHubPath,
  guidePath
} from '../../content/shopify/guideLocale';
import { standalonePage } from '../../content/shopify/standalone';
import { loadMessages } from '../../utils/i18n';

const locales = ['en', 'pl', 'uk'];

export default function ShopifyGuides({ pages, locale }) {
  const ui = getGuideUi(locale);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ui.all);
  const categories = [ui.all, ...new Set(pages.map((page) => page.category))];
  const visible = pages.filter(
    (page) =>
      (category === ui.all || page.category === category) &&
      `${page.title} ${page.description} ${page.toolLabel}`
        .toLowerCase()
        .includes(query.toLowerCase().trim())
  );
  const alternatePaths = Object.fromEntries(
    locales.map((item) => [item, guideHubPath(item)])
  );
  return (
    <div lang={locale} className="bg-slate-50 px-6 py-14 text-slate-900">
      <ShopifySEO
        title={`${ui.hubTitle} | Afivio`}
        description={ui.hubDescription}
        path={guideHubPath(locale)}
        locale={locale}
        alternatePaths={alternatePaths}
        breadcrumb={{
          home: ui.home,
          hub: ui.hub,
          hubPath: guideHubPath(locale)
        }}
      />
      <div className="mx-auto max-w-6xl">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-blue-700">
          {ui.hubEyebrow}
        </p>
        <h1 className="max-w-4xl text-4xl font-bold leading-tight md:text-6xl">
          {ui.hubTitle}
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600">
          {ui.hubLead}
        </p>
        <div className="my-8 flex flex-wrap gap-5">
          <Link
            href={guidePath('start-affiliate-program', locale)}
            locale={false}
            className="rounded-xl bg-blue-700 px-6 py-3 text-white"
          >
            {ui.start}
          </Link>
          <Link
            href="/shopify-integration"
            locale={locale}
            className="py-3 text-blue-700 underline"
          >
            {ui.integration}
          </Link>
        </div>
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-6">
          <label htmlFor="resource-search" className="mb-2 block font-semibold">
            {ui.find}
          </label>
          <input
            id="resource-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={ui.placeholder}
            className="w-full rounded-lg border border-slate-300 p-3"
          />
          <div
            className="mt-5 flex flex-wrap gap-2"
            role="group"
            aria-label={ui.filter}
          >
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
                className={`rounded-full border px-4 py-2 text-sm ${
                  category === item
                    ? 'border-blue-700 bg-blue-700 text-white'
                    : 'border-slate-300 text-slate-700 hover:border-blue-700'
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
        <p className="mb-5 text-sm text-slate-600" role="status">
          {visible.length} {ui.resources}
        </p>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((page) => (
            <article
              key={page.slug}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-7"
            >
              <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-blue-700">
                {page.category}
              </p>
              <h2 className="mb-4 text-xl font-bold leading-7">
                <Link
                  locale={false}
                  href={guidePath(page.slug, locale)}
                  className="hover:text-blue-700"
                >
                  {page.title}
                </Link>
              </h2>
              <p className="mb-6 text-sm leading-7 text-slate-600">
                {page.description}
              </p>
              <Link
                locale={false}
                href={guidePath(page.slug, locale)}
                className="mt-auto text-sm font-semibold text-blue-700"
              >
                {page.toolLabel} →
              </Link>
            </article>
          ))}
        </div>
        {visible.length === 0 && (
          <p className="rounded-xl bg-white p-8">{ui.noResults}</p>
        )}
      </div>
    </div>
  );
}

export async function getStaticProps({ locale = 'en' }) {
  if (!locales.includes(locale)) return { notFound: true };
  const pages = shopifyPages.map((page) => {
    if (locale === 'en')
      return {
        slug: page.slug,
        title: page.title,
        description: page.description,
        category: page.category,
        toolLabel: page.tool.title
      };
    const localized = standalonePage(page.slug, locale);
    return {
      slug: localized.slug,
      title: localized.title,
      description: localized.description,
      category: localized.category,
      toolLabel: localized.tool.title
    };
  });
  return {
    props: {
      pages,
      locale,
      messages: await loadMessages(locale, {
        includeShopifyStandalone: true,
        includeShopifyGuides: true
      })
    }
  };
}
